/**
 * Cmd-K Quick-Search Palette — global fuzzy search across referrers,
 * practitioners, SOPs, prep guides, MBS items, incidents, and staff.
 *
 * Mounted once in <App>; opened with Cmd+K / Ctrl+K from anywhere. ↑/↓ to
 * navigate the flat result list, Enter to open, Esc to close.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Search, X, Loader2, Building2, User, ClipboardCheck, ListChecks,
  Activity, AlertTriangle, Users, ArrowUp, ArrowDown, CornerDownLeft,
  Compass, History,
} from 'lucide-react';
import { DESTINATIONS } from '../lib/navRegistry';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RECENT_PAGES_KEY = 'i3:cmdk:recent';
const MAX_RECENT = 5;

function pushRecentPage(id) {
  try {
    const cur = JSON.parse(localStorage.getItem(RECENT_PAGES_KEY) || '[]');
    const next = [id, ...cur.filter(x => x !== id)].slice(0, MAX_RECENT);
    localStorage.setItem(RECENT_PAGES_KEY, JSON.stringify(next));
  } catch { /* ignore */ }
}
function getRecentPages() {
  try { return JSON.parse(localStorage.getItem(RECENT_PAGES_KEY) || '[]'); }
  catch { return []; }
}

const GROUP_META = {
  pages:         { label: 'Pages',             icon: Compass,        color: '#0d6efd' },
  referrers:     { label: 'Referring clinics', icon: Building2,      color: '#7C3AED' },
  practitioners: { label: 'Practitioners',     icon: User,           color: '#7C3AED' },
  sops:          { label: 'SOPs',              icon: ClipboardCheck, color: '#0EA5E9' },
  preps:         { label: 'Prep guides',       icon: ListChecks,     color: '#16A34A' },
  mbs:           { label: 'MBS items',         icon: Activity,       color: '#D97706' },
  incidents:     { label: 'Incidents',         icon: AlertTriangle,  color: '#DC2626' },
  staff:         { label: 'People',            icon: Users,          color: '#0EA5E9' },
};

const GROUP_ORDER = ['pages', 'referrers', 'practitioners', 'sops', 'preps', 'mbs', 'incidents', 'staff'];

// Score a navigation destination against the query.
function scorePage(d, q) {
  if (!q) return 1;  // any non-empty list when there's no query
  const ql = q.toLowerCase();
  const label = d.label.toLowerCase();
  if (label === ql) return 100;
  if (label.startsWith(ql)) return 70;
  if (label.includes(ql)) return 50;
  if ((d.blurb || '').toLowerCase().includes(ql)) return 30;
  if ((d.keywords || []).some(k => k.toLowerCase().includes(ql))) return 25;
  return 0;
}

export default function QuickSearchPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [groups, setGroups] = useState({});
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';

  // Global Cmd+K / Ctrl+K listener — opens the palette from anywhere.
  // Also listens for a window-level 'open-command-palette' event so other
  // components (e.g., header search pill) can trigger it programmatically.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    const onExternal = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('open-command-palette', onExternal);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('open-command-palette', onExternal);
    };
  }, [open]);

  // Focus the input each time we open
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 30);
      setHighlight(0);
    } else {
      setQ('');
      setGroups({});
    }
  }, [open]);

  // Debounced fetch — only for backend-driven content groups.
  // The 'pages' group is computed locally (instant) so even an empty query
  // shows a useful list.
  useEffect(() => {
    if (!open) return;
    if (q.trim().length < 2) { setGroups({}); return; }
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await axios.get(`${API}/quick-search`, {
          params: { q: q.trim() }, withCredentials: true,
        });
        setGroups(r.data.groups || {});
        setHighlight(0);
      } catch { /* swallow — palette stays empty */ }
      setBusy(false);
    }, 180);
    return () => clearTimeout(t);
  }, [q, open]);

  // Compute the local 'pages' group (navigation destinations + recents).
  // Always present unless an explicit query yields zero page matches.
  const { canDestination, loaded: permsLoaded } = usePermissions();
  // Two-tier filter: registry's static role rule, then dynamic permission grant.
  const pageDestinations = DESTINATIONS
    .filter(d => d.role === 'all' || isPM)
    .filter(d => !permsLoaded || canDestination(d.id));
  let pagesGroup = [];
  if (!q.trim()) {
    // Empty query: show recents first, then a curated selection of common pages
    const recentIds = getRecentPages();
    const recents = recentIds.map(id => pageDestinations.find(d => d.id === id)).filter(Boolean);
    const remaining = pageDestinations.filter(d => !recentIds.includes(d.id)).slice(0, 6);
    pagesGroup = [
      ...recents.map(d => ({ ...d, _isRecent: true })),
      ...remaining,
    ];
  } else {
    pagesGroup = pageDestinations
      .map(d => ({ ...d, _score: scorePage(d, q) }))
      .filter(d => d._score > 0)
      .sort((a, b) => b._score - a._score);
  }
  // Map pages onto the shape used by the result renderer
  const pagesAsItems = pagesGroup.map(p => ({
    title: p.label,
    subtitle: p.blurb,
    href: p.href,
    _pageId: p.id,
    _isRecent: p._isRecent,
  }));

  // Flatten groups in display order so up/down can move through everything
  const allGroups = { ...groups, pages: pagesAsItems };
  const flat = [];
  GROUP_ORDER.forEach(key => {
    (allGroups[key] || []).forEach((item, gi) => flat.push({ ...item, _gkey: key, _gi: gi }));
  });

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(flat.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      const sel = flat[highlight];
      if (sel?.href) {
        if (sel._pageId) pushRecentPage(sel._pageId);
        navigate(sel.href);
        setOpen(false);
      }
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh] px-4"
      data-testid="quick-search-palette">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="relative w-full max-w-2xl bg-card border border-border rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: '70vh' }}>
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <Search className="w-4 h-4 text-muted-foreground shrink-0" />
          <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={onKeyDown} data-testid="quick-search-input"
            placeholder="Search clinics, practitioners, SOPs, prep guides, MBS items…"
            className="flex-1 bg-transparent border-0 outline-none text-base text-foreground placeholder:text-muted-foreground" />
          {busy && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
          <button onClick={() => setOpen(false)} data-testid="quick-search-close"
            className="text-muted-foreground hover:text-foreground p-1 rounded-md border border-border">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto">
          {flat.length === 0 && !busy ? (
            <div className="px-4 py-12 text-center text-sm text-muted-foreground" data-testid="quick-search-empty">
              {q.trim().length < 2
                ? <EmptyState />
                : <>No results for <span className="font-mono text-foreground">{q}</span>.</>}
            </div>
          ) : (
            <div className="py-2">
              {GROUP_ORDER.map(key => {
                const items = allGroups[key] || [];
                if (!items.length) return null;
                const meta = GROUP_META[key];
                const Icon = meta.icon;
                return (
                  <div key={key} className="mb-1">
                    <div className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1.5"
                      style={{ color: meta.color }}>
                      <Icon className="w-3 h-3" /> {meta.label} · {items.length}
                    </div>
                    {items.map((item, i) => {
                      const flatIdx = flat.findIndex(f => f._gkey === key && f._gi === i);
                      const active = flatIdx === highlight;
                      return (
                        <button key={`${key}-${i}`}
                          data-testid={`quick-search-result-${key}-${i}`}
                          onMouseEnter={() => setHighlight(flatIdx)}
                          onClick={() => {
                            if (item._pageId) pushRecentPage(item._pageId);
                            navigate(item.href); setOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2 flex items-start gap-3 transition-colors
                            ${active ? 'bg-accent' : 'hover:bg-accent/50'}`}>
                          <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                            style={{ backgroundColor: `${meta.color}15`, color: meta.color }}>
                            <Icon className="w-3.5 h-3.5" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-foreground font-medium truncate inline-flex items-center gap-1.5">
                              {item.title || '(untitled)'}
                              {item._isRecent && <History className="w-3 h-3 text-muted-foreground" />}
                            </p>
                            {item.subtitle && <p className="text-xs text-muted-foreground truncate">{item.subtitle}</p>}
                          </div>
                          {active && (
                            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1 shrink-0 mt-1">
                              <CornerDownLeft className="w-3 h-3" /> open
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="px-4 py-2 border-t border-border bg-muted/30 flex items-center justify-between text-[10px] text-muted-foreground">
          <span className="inline-flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><ArrowUp className="w-3 h-3" /><ArrowDown className="w-3 h-3" /> navigate</span>
            <span className="inline-flex items-center gap-1"><CornerDownLeft className="w-3 h-3" /> open</span>
            <span><kbd className="px-1.5 py-0.5 bg-card border border-border rounded text-[9px] font-mono">esc</kbd> close</span>
          </span>
          <span className="font-mono">⌘K / Ctrl-K</span>
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  const examples = [
    { icon: Building2, label: 'Clinic name or suburb', sample: 'Wollongong' },
    { icon: User, label: 'Practitioner name', sample: 'Smith' },
    { icon: ClipboardCheck, label: 'SOP title', sample: 'IV contrast' },
    { icon: Activity, label: 'MBS item number', sample: '55065' },
  ];
  return (
    <div className="px-4 py-6">
      <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Try searching for</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {examples.map(e => (
          <div key={e.label} className="flex items-start gap-2.5 p-2.5 bg-muted/40 border border-border rounded-lg">
            <e.icon className="w-3.5 h-3.5 mt-0.5 text-muted-foreground shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-foreground font-medium">{e.label}</p>
              <p className="text-[11px] text-muted-foreground font-mono">e.g. {e.sample}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
